import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useToast } from '../components/Admissionform/ToastManager';
import { useParams, useNavigate } from 'react-router-dom';
import TabNavigation from '../components/common/TabNavigation ';
import EditJobDetails from '../components/JobRequirements/EditJobDetails';
import MatchingStudents from '../components/JobRequirements/MatchingStudents';
import MappedStudents from '../components/JobRequirements/MappedStudents';
import ViewedUsers from '../components/JobRequirements/ViewedUsers';
import JobShareActions from "../components/JobRequirements/Editjob.js/JobShareActions";
import './JobRequirementForm.css';

const EditJobRequirement = () => {
    const { jobReqId } = useParams();
    const navigate = useNavigate();
    const hasFeedbackGiven = useRef(false);
    const navigateAfterFeedback = useRef(null);

    const getInitialTab = () => {
        const lastPath = sessionStorage.getItem('lastJobReqPath');
        const currentPath = window.location.pathname;
        if (lastPath === currentPath) {
            const savedTab = sessionStorage.getItem('lastActiveTab');
            sessionStorage.removeItem('lastJobReqPath');
            sessionStorage.removeItem('lastActiveTab');
            return savedTab || 'editJobDetails';
        }
        return 'editJobDetails';
    };

    const [activeTab, setActiveTab] = useState(getInitialTab);
    const { showSuccess, showError, showWarning } = useToast();
    const loggedInUserRole = localStorage.getItem("role");
    const [formData, setFormData] = useState({
        jobReqId: '',
        createdDate: '',
        createdBy: '',
        jobReqStatus: 'Open',
        jobTitle: '',
        jobDescription: '',
        companyName: '',
        jobLocation: '',
        qualification: '',
        passedOutYear: '',
        experienceRequired: '',
        additionalSkills: '',
        salary: '',
        bond: '',
        bondDetails: '',
        interviewType: '',
        directApplyLink: '',
        modeOfInterview: '',
        numberOfOpenings: '',
        numberOfRounds: '',
        interviewDate: '',
        interviewTime: '',
        interviewLocation: '',
        venue: '',
        additionalDetails: '',
        companyHRName: '',
        companyHRContact: '',
        companyHREmail: '',
        feedbackForClosure: '',
        totalSelectionCount: '',
        feedbackForFreeze: '',
        createdContact: '',
        allowedBranches: '',
        gender: '',
    });

    const [department, setDepartment] = useState('');
    const [skillsKnown, setSkillsKnown] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const loggedInUserName = localStorage.getItem("emp_name");
    const isReadOnly = loggedInUserName !== formData.createdBy;
    const [allBranches, setAllBranches] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const hasFetchedBranches = useRef(false);
    const hasFetchedJob = useRef(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState("");
    const [feedbackText, setFeedbackText] = useState("");
    const [showClosureModal, setShowClosureModal] = useState(false);

    const [closureSummary, setClosureSummary] = useState({
        statusCounts: {},
        students: []
    });
    const [mappedStudentFeedback, setMappedStudentFeedback] = useState("");

    const [pendingApiData, setPendingApiData] = useState(null);

    const canViewHRFields =
        loggedInUserName === formData.createdBy || loggedInUserRole === "Super-Admin";

    const tabs = [
        { id: 'editJobDetails', label: 'Edit Job Details' },
        {
            id: 'matchingStudents',
            label: 'Matching Students',
            disabled: formData.jobReqStatus === 'Closed' || formData.jobReqStatus === 'Cancelled' || formData.jobReqStatus === 'Freeze',
            disabledReason: formData.jobReqStatus === 'Freeze'
                ? 'This job is frozen'
                : 'This job is closed/cancelled'
        },
        { id: 'mappedStudents', label: 'Mapped Students' },
        { id: 'viewedUsers', label: 'Action Log' },
    ];

    const experienceLevels = ['Fresher', '<1year', '1-2years', '2-3years', '>3years'];
    const interviewTypes = ['Walk-in', 'Pumo Campus Drive', 'Direct Apply', 'Pumo Scheduled Interview'];
    const interviewModes = ['In-Person', 'Virtual'];
    const rounds = ['1', '2', '3'];

    const guardedNavigate = useCallback((path) => {
        // Owner or already given feedback → navigate freely
        if (
            formData.jobReqStatus !== 'Open' ||
            hasFeedbackGiven.current ||
            loggedInUserName === formData.createdBy  // ← ADD THIS
        ) {
            navigate(path);
            return;
        }
        navigateAfterFeedback.current = path;
        setShowFeedbackModal(true);
    }, [navigate, formData.jobReqStatus, loggedInUserName, formData.createdBy]);

    // Expose globally so ProfileAvatar's safeNavigate can use it
    useEffect(() => {
        window.__guardedNavigate = guardedNavigate;
        return () => { window.__guardedNavigate = null; };
    }, [guardedNavigate]);

    // Block browser back/refresh
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (
                formData.jobReqStatus === 'Open' &&
                !hasFeedbackGiven.current &&
                loggedInUserName !== formData.createdBy  // ← ADD THIS
            ) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [formData.jobReqStatus, formData.createdBy, loggedInUserName]);
    // Block browser back button — force to /job-dashboard
    useEffect(() => {
        if (
            formData.jobReqStatus !== 'Open' ||
            hasFeedbackGiven.current ||
            loggedInUserName === formData.createdBy
        ) return;

        // Push only ONE extra state entry
        window.history.pushState(null, "", window.location.pathname);

        const handlePopState = () => {
            if (hasFeedbackGiven.current || loggedInUserName === formData.createdBy) {
                navigate("/job-dashboard");
                return;
            }
            // Don't push state again — just show the modal
            // The single pushState above is enough to intercept the back button
            navigateAfterFeedback.current = "/job-dashboard";
            setShowFeedbackModal(true);
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [navigate, formData.jobReqStatus, formData.createdBy, loggedInUserName]);
    // ↑ showFeedbackModal removed from deps intentionally
    useEffect(() => {
        if (
            activeTab === 'matchingStudents' &&
            (formData.jobReqStatus === 'Closed' ||
                formData.jobReqStatus === 'Cancelled' ||
                formData.jobReqStatus === 'Freeze')
        ) {
            setActiveTab('editJobDetails');
        }
    }, [formData.jobReqStatus, activeTab]);
    // Add this effect after your existing useEffects
    useEffect(() => {
        if (!jobReqId || formData.jobReqStatus !== 'Open') return;

        const checkFeedbackStatus = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/job-feedback/status/${jobReqId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.ok) {
                    const data = await res.json();
                    if (data.alreadyGiven) {
                        hasFeedbackGiven.current = true;
                    }
                }
            } catch (err) {
                console.error('Failed to check feedback status:', err);
            }
        };

        checkFeedbackStatus();
    }, [jobReqId, formData.jobReqStatus]); // runs again if status changes

    // Fetch branches
    useEffect(() => {
        if (hasFetchedBranches.current) return;

        const fetchBranches = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/location`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.ok) {
                    const data = await response.json();
                    const activeBranches = data.filter(loc => loc.status === true);
                    setAllBranches(activeBranches);
                    hasFetchedBranches.current = true;
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
                showError('Failed to load branches');
            }
        };

        fetchBranches();
    }, []);

    // Fetch job data
    useEffect(() => {
        if (!jobReqId || hasFetchedJob.current) return;

        const fetchJobRequirement = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    showError("Session expired. Please login again.");
                    navigate('/login');
                    return;
                }

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/placements/${jobReqId}`,
                    { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
                );

                if (response.ok) {
                    const result = await response.json();
                    const data = result.data;

                    setFormData({
                        jobReqId: data.jobReqId || '',
                        createdDate: data.createdAt
                            ? new Date(data.createdAt).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true
                            }).replace(",", "")
                            : '',
                        createdBy: data.createdBy || '',
                        createdContact: data.createdContact || '',
                        jobReqStatus: data.jobReqStatus || 'Open',
                        jobTitle: data.jobTitle || '',
                        jobDescription: data.jobDescription || '',
                        companyName: data.companyName || '',
                        jobLocation: data.jobLocation || '',
                        qualification: data.qualification || '',
                        passedOutYear: data.passedOutYear || '',
                        experienceRequired: data.experienceRequired || '',
                        salary: data.salary || '',
                        bond: data.bond || '',
                        bondDetails: data.bondDetails || '',
                        interviewType: data.interviewType || '',
                        directApplyLink: data.directApplyLink || '',
                        modeOfInterview: data.modeOfInterview || '',
                        numberOfOpenings: data.numberOfOpenings || '',
                        numberOfRounds: data.numberOfRounds || '',
                        interviewDate: data.interviewDate || '',
                        interviewTime: data.interviewTime || '',
                        interviewLocation: data.interviewLocation || '',
                        venue: data.venue || '',
                        additionalDetails: data.additionalDetails || '',
                        additionalSkills: data.additionalSkills || '',
                        companyHRName: data.companyHRName || '',
                        companyHRContact: data.companyHRContact || '',
                        companyHREmail: data.companyHREmail || '',
                        feedbackForClosure: data.feedbackForClosure || '',
                        totalSelectionCount: data.totalSelectionCount || '',
                        feedbackForFreeze: data.feedbackForFreeze || '',
                        allowedBranches: data.allowedBranches || '',
                        gender: data.gender || '',
                    });

                    setDepartment(data.department || '');
                    setSkillsKnown(
                        data.skillSet
                            ? data.skillSet.includes(" || ")
                                ? data.skillSet.split(" || ").map(s => s.trim())
                                : data.skillSet.split(", ").map(s => s.trim())
                            : []
                    ); const branches = data.allowedBranches
                        ? (data.allowedBranches === "All Branches"
                            ? []
                            : data.allowedBranches.split(", "))
                        : [];
                    setSelectedBranches(branches);
                    hasFetchedJob.current = true;
                } else {
                    showError('Failed to fetch job requirement');
                    navigate('/job-requirements');
                }
            } catch (error) {
                console.error('Error:', error);
                showError('Failed to load job requirement data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchJobRequirement();
    }, [jobReqId]);

    useEffect(() => {
        if (allBranches.length > 0 && formData.jobReqId && selectedBranches.length === 0) {
            const fetchAllowedBranches = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/placements/${jobReqId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        const allowedBranches = result.data.allowedBranches;
                        if (allowedBranches === "All Branches") {
                            setSelectedBranches(allBranches.map(b => b.branch));
                        } else if (allowedBranches && typeof allowedBranches === 'string') {
                            setSelectedBranches(allowedBranches.split(", "));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branches:', error);
                }
            };
            fetchAllowedBranches();
        }
    }, [allBranches, formData.jobReqId, jobReqId]);

    const handleFormChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async () => {
        const requiredFields = [
            'jobTitle', 'jobDescription', 'companyName',
            'jobLocation', 'qualification',
            'passedOutYear', 'experienceRequired', 'salary', 'bond',
            'interviewType', 'modeOfInterview',
            'numberOfOpenings', 'numberOfRounds'
        ];

        const missingFields = requiredFields.filter(field => !formData[field]);

        if (!department) missingFields.push('department');
        if (skillsKnown.length === 0) missingFields.push('skillSet');
        if (selectedBranches.length === 0) missingFields.push('allowedBranches');

        if (formData.bond === 'Yes' && !formData.bondDetails) {
            missingFields.push('bondDetails');
        }
        if (formData.interviewType === 'Direct Apply' && !formData.directApplyLink) {
            missingFields.push('directApplyLink');
        }
        if (formData.interviewType === 'Pumo Campus Drive') {
            if (!formData.interviewDate) missingFields.push('interviewDate');
            if (!formData.interviewTime) missingFields.push('interviewTime');
            if (!formData.venue) missingFields.push('venue');
        }
        if (formData.jobReqStatus === 'Closed' && !formData.totalSelectionCount) {
            showWarning('Total Selection Count is required when closing a job requirement');
            return;
        }
        if (
            (formData.jobReqStatus === 'Closed' || formData.jobReqStatus === 'Cancelled') &&
            !formData.feedbackForClosure
        ) {
            showWarning('Feedback is required when closing or cancelling a job requirement');
            return;
        }
        if (missingFields.length > 0) {
            showWarning(`Please fill all required fields: ${missingFields.join(', ')}`);
            return;
        }
        if (formData.jobReqStatus === 'Freeze' && !formData.feedbackForFreeze) {
            showWarning('Feedback is required when freezing a job requirement');
            return;
        }

        setIsSubmitting(true);

        const sanitizeNumber = (value) => {
            if (value === "" || value === null || value === undefined) return null;
            const num = Number(value);
            return isNaN(num) ? null : num;
        };

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showError("Session expired. Please login again.");
                navigate('/login');
                return;
            }

            const apiData = {
                jobReqStatus: formData.jobReqStatus,
                jobTitle: formData.jobTitle,
                jobDescription: formData.jobDescription,
                companyName: formData.companyName,
                jobLocation: formData.jobLocation,
                qualification: formData.qualification,
                passedOutYear: formData.passedOutYear,
                experienceRequired: formData.experienceRequired,
                salary: formData.salary,
                bond: formData.bond,
                bondDetails: formData.bondDetails || null,
                interviewType: formData.interviewType,
                directApplyLink: formData.directApplyLink || null,
                modeOfInterview: formData.modeOfInterview,
                numberOfOpenings: sanitizeNumber(formData.numberOfOpenings),
                numberOfRounds: sanitizeNumber(formData.numberOfRounds),
                totalSelectionCount: formData.jobReqStatus === 'Closed'
                    ? sanitizeNumber(formData.totalSelectionCount)
                    : null,
                interviewDate: formData.interviewDate || null,
                interviewTime: formData.interviewTime || null,
                interviewLocation: formData.interviewLocation || null,
                venue: formData.venue || null,
                additionalSkills: formData.additionalSkills || null,
                additionalDetails: formData.additionalDetails || null,
                companyHRName: formData.companyHRName || null,
                companyHRContact: formData.companyHRContact || null,
                companyHREmail: formData.companyHREmail || null,
                department: department,
                skillSet: skillsKnown.join(" || "),
                allowedBranches:
                    selectedBranches.length === allBranches.length
                        ? "All Branches"
                        : selectedBranches.join(", "),
                gender: formData.gender || null,
                feedbackForClosure: formData.feedbackForClosure || null,
                feedbackForFreeze:
                    formData.jobReqStatus === "Freeze"
                        ? formData.feedbackForFreeze
                        : null,
            };
            if (
                formData.jobReqStatus === "Closed"
            ) {

                if (!mappedStudentFeedback.trim()) {

                    try {

                        const token =
                            localStorage.getItem("token");

                        const response = await fetch(
                            `${process.env.REACT_APP_API_URL}/placements/${jobReqId}/closure-summary`,
                            {
                                headers: {
                                    Authorization: `Bearer ${token}`
                                }
                            }
                        );

                        const data = await response.json();

                        if (response.ok) {

                            setClosureSummary(data);

                            setPendingApiData(apiData);

                            setShowClosureModal(true);

                            setIsSubmitting(false);

                            return;
                        }

                    } catch (error) {

                        console.error(error);

                        showError(
                            "Failed to load closure summary"
                        );

                        setIsSubmitting(false);

                        return;
                    }
                }
            }
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/update/placements/${jobReqId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(apiData)
                }
            );

            const result = await response.json();

            if (response.ok) {
                showSuccess('Job requirement updated successfully!');
            } else {
                if (response.status === 403) {
                    showError('You are not authorized to update this job requirement');
                } else {
                    showError(result?.message || 'Failed to update job requirement');
                }
            }
        } catch (error) {
            console.error('Error:', error);
            showError('Failed to update. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    const confirmClosureUpdate = async () => {

        if (!mappedStudentFeedback.trim()) {

            showWarning(
                "Feedback is mandatory"
            );

            return;
        }

        try {

            const token =
                localStorage.getItem("token");

            const finalData = {
                ...pendingApiData,
                mappedStudentFeedback
            };

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/update/placements/${jobReqId}`,
                {
                    method: "PUT",

                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },

                    body: JSON.stringify(finalData)
                }
            );

            const result =
                await response.json();

            if (response.ok) {

                showSuccess(
                    "Requirement closed successfully"
                );

                setShowClosureModal(false);

                setMappedStudentFeedback("");

            } else {

                showError(
                    result.message ||
                    "Failed to close requirement"
                );
            }

        } catch (error) {

            console.error(error);

            showError(
                "Failed to close requirement"
            );
        }
    };
    const submitFeedback = async () => {
        const trimmedText = feedbackText.trim();

        if (!selectedOption && trimmedText.length < 10) {
            showWarning(
                trimmedText.length === 0
                    ? "Please select an option or enter your feedback (min 10 characters)"
                    : `Feedback too short — need at least ${10 - trimmedText.length} more character(s)`
            );
            return;
        }

        const finalFeedback = selectedOption
            ? selectedOption + (trimmedText ? ` - ${trimmedText}` : "")
            : trimmedText;

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${process.env.REACT_APP_API_URL}/job-feedback`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ jobReqId, feedback: finalFeedback })
            });

            if (!res.ok) throw new Error();

            hasFeedbackGiven.current = true;

            setShowFeedbackModal(false);
            setSelectedOption("");
            setFeedbackText("");

            const destination = navigateAfterFeedback.current;
            navigateAfterFeedback.current = null;
            if (destination) navigate(destination);

        } catch {
            showError("Failed to submit feedback");
        }
    };

    const handleCancel = () => {
        guardedNavigate("/job-dashboard");
    };

    const jobDetailsForMatching = useMemo(() => ({
        jobReqId: formData.jobReqId || jobReqId,
        jobTitle: formData.jobTitle || 'Loading...',
        jobDescription: formData.jobDescription || 'Loading...',
        companyName: formData.companyName || 'Loading...',
        jobLocation: formData.jobLocation || 'Loading...',
        skillSet: skillsKnown.length > 0 ? skillsKnown.join(", ") : 'Loading...',
        qualification: formData.qualification || 'Loading...',
        passedOutYear: formData.passedOutYear || 'Loading...',
        experienceRequired: formData.experienceRequired || 'Loading...',
        bond: formData.bond || 'Loading...',
        interviewType: formData.interviewType || 'Loading...',
        numberOfOpenings: formData.numberOfOpenings || 'Loading...',
        department: department || '',
        interviewDate: formData.interviewDate || 'Loading...',
        allowedBranches: selectedBranches.length === allBranches.length
            ? "All Branches"
            : selectedBranches.join(", "),
    }), [
        formData.jobReqId, formData.jobTitle, formData.jobDescription,
        formData.companyName, formData.jobLocation, formData.qualification,
        formData.passedOutYear, formData.experienceRequired, formData.bond,
        formData.interviewType, formData.numberOfOpenings, formData.interviewDate,
        department, skillsKnown, selectedBranches, allBranches.length, jobReqId
    ]);

    if (isLoading) {
        return (
            <div className="jobcontainer">
                <div className="pagesf-wrapper">
                    <div className="form">
                        <div style={{ padding: '32px', textAlign: 'center' }}>
                            <p>Loading job requirement data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="jobcontainer">
            <div className="pagesf-wrapper">
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                <div className="form">
                    <div className="tab-jobcontent">
                        {activeTab === 'editJobDetails' && (
                            <div className="tab-panel">
                                <EditJobDetails
                                    formData={formData}
                                    department={department}
                                    skillsKnown={skillsKnown}
                                    onFormChange={handleFormChange}
                                    onDepartmentChange={setDepartment}
                                    onSkillsChange={setSkillsKnown}
                                    allBranches={allBranches}
                                    selectedBranches={selectedBranches}
                                    onBranchesChange={setSelectedBranches}
                                    experienceLevels={experienceLevels}
                                    interviewTypes={interviewTypes}
                                    interviewModes={interviewModes}
                                    rounds={rounds}
                                    isReadOnly={isReadOnly}
                                    canViewHRFields={canViewHRFields}
                                />
                            </div>
                        )}

                        {activeTab === 'matchingStudents' && (
                            <div className="tab-panel">
                                <MatchingStudents
                                    jobReqId={jobReqId}
                                    jobDetails={jobDetailsForMatching}
                                />
                            </div>
                        )}

                        {activeTab === 'mappedStudents' && (
                            <div className="tab-panel">
                                <MappedStudents
                                    jobReqId={jobReqId}
                                    jobDetails={jobDetailsForMatching}
                                />
                            </div>
                        )}
                    </div>

                    {activeTab === 'editJobDetails' && (
                        <div className="form-actions">
                            <JobShareActions
                                formData={formData}
                                department={department}
                                skillsKnown={skillsKnown}
                                selectedBranches={selectedBranches}
                                allBranches={allBranches}
                                showSuccess={showSuccess}
                                showError={showError}
                            />
                            {!isReadOnly && (
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            )}
                            {!isReadOnly && (
                                <button
                                    type="button"
                                    className="submit-btn"
                                    onClick={handleUpdate}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Updating...' : 'Update Job Details'}
                                </button>
                            )}
                        </div>
                    )}

                    {activeTab === 'viewedUsers' && (
                        <div className="tab-panel">
                            <ViewedUsers jobReqId={jobReqId} />
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ Feedback Modal */}
            {showFeedbackModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Give Feedback (Mandatory)</h3>

                        <div className="feedback-options">
                            <label>
                                <input
                                    type="radio"
                                    value="No students available"
                                    checked={selectedOption === "No students available"}
                                    onChange={(e) => setSelectedOption(e.target.value)}
                                />
                                No students available for this requirement
                            </label>

                            <label>
                                <input
                                    type="radio"
                                    value="Mapped students"
                                    checked={selectedOption === "Mapped students"}
                                    onChange={(e) => setSelectedOption(e.target.value)}
                                />
                                I have mapped students
                            </label>
                        </div>

                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Additional comments (optional)"
                        />

                        <div className="modal-actions">
                            <button onClick={submitFeedback}>
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showClosureModal && (
                <div className="closure-modal-overlay">

                    <div className="closure-modal">

                        <div className="closure-header">

                            <h2>
                                Requirement Closure Summary
                            </h2>

                            <p className="closure-warning-text">
                                You are about to close this requirement.
                                Please verify all mapped student statuses before continuing.
                                Students in{" "}
                                <span className="closure-highlight-status">
                                    Assigned, Acknowledged, Shortlisted,
                                    and Interview Scheduled
                                </span>{" "}
                                statuses will be changed to Cancelled.
                                The feedback entered below will be saved for those students.
                            </p>
                        </div>

                        <div className="closure-section">

                            <h3>Status Counts</h3>

                            <table className="closure-table">

                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {Object.entries(
                                        closureSummary.statusCounts
                                    ).map(([status, count]) => (

                                        <tr key={status}>
                                            <td>{status}</td>
                                            <td>{count}</td>
                                        </tr>

                                    ))}

                                </tbody>

                            </table>
                        </div>

                        <div className="closure-section">

                            <h3>Students</h3>

                            <div className="closure-student-wrapper">

                                <table className="closure-table">

                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Branch</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {closureSummary.students.map(
                                            (student, index) => (

                                                <tr key={index}>
                                                    <td>{student.studentName}</td>
                                                    <td>{student.branch}</td>
                                                    <td>{student.currentStatus}</td>
                                                </tr>

                                            )
                                        )}

                                    </tbody>

                                </table>

                            </div>
                        </div>

                        <div className="closure-section">

                            <label className="closure-label">
                                Cancellation Remarks
                                <span>*</span>
                            </label>

                            <textarea
                                className="closure-textarea"
                                value={mappedStudentFeedback}
                                onChange={(e) =>
                                    setMappedStudentFeedback(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter cancellation remarks to be saved as feedback for affected students"
                            />
                        </div>

                        <div className="closure-actions">

                            <button
                                className="closure-cancel-btn"
                                onClick={() => {
                                    setShowClosureModal(false);
                                    setMappedStudentFeedback("");
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="closure-submit-btn"
                                onClick={confirmClosureUpdate}
                            >
                                Confirm Closure
                            </button>

                        </div>

                    </div>

                </div>
            )}
        </div>
    );
};

export default EditJobRequirement;